import { Col, Divider, Input, Radio, Row, Select, Space } from 'antd';
import React from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';

import {
  useMarketplaceCategories,
  useMarketplaceLanes,
} from '../../hooks/useMarketplaceOptions';
import type { MarketplaceRootData } from '../../schemas/rootSchema';
import { ProductType } from '../../types/types';
import { getProductTypeLabel } from '../../utils/utils';
import { FormItem } from '../FormItem';
import { MarketplaceImageUpload } from '../MarketplaceImageUpload';
import { Section } from '../Section';

const { TextArea } = Input;

export const CreationStep: React.FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<MarketplaceRootData>();

  const productType = useWatch({
    control,
    name: 'creation.productType',
  });

  const isCombo = productType === ProductType.Combo;

  const { options: categoryOptions, isLoading: isLoadingCategories } =
    useMarketplaceCategories();
  const { options: laneOptions, isLoading: isLoadingLanes } =
    useMarketplaceLanes();

  return (
    <Space direction='vertical' size={32} style={{ width: '100%' }}>
      <Section
        title='Informações Básicas'
        subtitle='Preencha os formulários abaixo para criar um novo item do tipo programa ou combo'
        type='primary'
      >
        <FormItem
          label='Tipo de Item'
          required
          control={control}
          name='creation.productType'
          renderItem={({ field }) => (
            <Radio.Group {...field}>
              <Radio value={ProductType.Program}>
                {getProductTypeLabel(ProductType.Program)}
              </Radio>
              <Radio value={ProductType.Combo}>
                {getProductTypeLabel(ProductType.Combo)}
              </Radio>
            </Radio.Group>
          )}
        />
        <Divider size='small' />
        <FormItem
          label={'Nome do item'}
          required
          control={control}
          name='creation.title'
          renderItem={({ field }) => (
            <Input
              {...field}
              placeholder={`Digite o nome do item (ex: ${isCombo ? 'Combo Nutricional' : 'Transformação Power'})`}
              maxLength={80}
              showCount
              status={get(errors, 'creation.title') ? 'error' : undefined}
            />
          )}
        />

        <FormItem
          label='Descrição'
          control={control}
          name='creation.description'
          renderItem={({ field }) => (
            <TextArea
              {...field}
              placeholder={`ex: Tenha acesso a todos os treinos focados em transformar a sua rotina.`}
              rows={4}
              maxLength={500}
              showCount
              status={get(errors, 'creation.description') ? 'error' : undefined}
            />
          )}
        />

        {isCombo && (
          <FormItem
            label='CTA (Texto do botão)'
            required
            control={control}
            name='creation.buttonText'
            renderItem={({ field }) => (
              <Input
                {...field}
                placeholder='Assinar'
                status={
                  get(errors, 'creation.buttonText') ? 'error' : undefined
                }
              />
            )}
          />
        )}

        <FormItem
          label='Categoria'
          required
          control={control}
          name='creation.categoryIds'
          renderItem={({ field }) => (
            <Select
              placeholder='Selecione uma categoria...'
              options={categoryOptions}
              value={field.value?.[0]}
              onChange={value => field.onChange(value ? [value] : [])}
              onBlur={field.onBlur}
              allowClear
              loading={isLoadingCategories}
              style={{ width: '100%' }}
              status={get(errors, 'creation.categoryIds') ? 'error' : undefined}
            />
          )}
        />
      </Section>

      <Section
        title='Gerenciamento de Imagens'
        subtitle={`Adicione as imagens do ${isCombo ? 'combo' : 'programa'} em diferentes formatos`}
        type='primary'
      >
        <Row gutter={[16, 16]}>
          <Col md={isCombo ? 6 : 8}>
            <MarketplaceImageUpload
              title={isCombo ? 'Imagem Card Principal (Web)' : 'Imagem web'}
              helperText='1125x634 pixels'
              required
              fieldName={
                isCombo
                  ? 'creation.images.combo.main'
                  : 'creation.images.program.card'
              }
              maxDimensions={{ width: 1125, height: 634 }}
            />
          </Col>
          <Col md={isCombo ? 6 : 8}>
            <MarketplaceImageUpload
              title={isCombo ? 'Imagem Mobile/Busca' : 'Imagem mobile'}
              helperText='591x840 pixels'
              required
              fieldName={
                isCombo
                  ? 'creation.images.combo.search'
                  : 'creation.images.program.vertical'
              }
              maxDimensions={{ width: 591, height: 840 }}
            />
          </Col>
          {isCombo ? (
            <>
              <Col md={6}>
                <MarketplaceImageUpload
                  title='Imagem Combo Programa (Web)'
                  helperText='168x96 pixels'
                  required
                  fieldName='creation.images.combo.highlight.original'
                  maxDimensions={{ width: 168, height: 96 }}
                />
              </Col>
              <Col md={6}>
                <MarketplaceImageUpload
                  title='Imagem Combo Programa (Mobile)'
                  helperText='96x96 pixels'
                  required
                  fieldName='creation.images.combo.highlight.small'
                  maxDimensions={{ width: 96, height: 96 }}
                />
              </Col>
            </>
          ) : (
            <Col md={8}>
              <MarketplaceImageUpload
                title='Logo'
                helperText='300x300 pixels'
                required
                fieldName='creation.images.program.logo'
                maxDimensions={{ width: 300, height: 300 }}
              />
            </Col>
          )}
        </Row>
      </Section>

      <Section title='Raias de Exibição' type='primary'>
        <FormItem
          label='Raias'
          required
          control={control}
          name='creation.laneIds'
          renderItem={({ field }) => (
            <Select
              {...field}
              mode='multiple'
              placeholder='Selecione...'
              options={laneOptions}
              loading={isLoadingLanes}
              style={{ width: '100%' }}
              status={get(errors, 'creation.laneIds') ? 'error' : undefined}
            />
          )}
        />
      </Section>
    </Space>
  );
};

export default CreationStep;
